import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import {
	Col,
	Empty,
	Flex,
	Row,
} from 'antd';
import { Moment } from 'moment';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
	Currencies,
	FuelTypes,
} from '@shared/utils/constants';
import { Values } from '@shared/utils/objectEnums';
import { filterUnique } from '@shared/utils/array';
import type { BunkerProps } from '@api/models/bunker';
import type { Port } from '@api/utils/ports';
import type { RobBunkerProps } from '@api/models/rob-bunker';
import type { ReturnStem } from '@api/features/voyages/bunker-stems/getBunkerStems';
import Card from '@client/components/Card/Card';
import { getRobs } from '@client/lib/api';
import Table from '@client/components/Table/Table';
import AddButton from '@client/components/AddButton';
import { Links } from '@client/utils/links';
import SummaryCardExtra from '@client/screens/fleet/VoyageDetailsScreen/components/SummaryCardExtra';
import LinkedContracts from '@client/components/LinkedContracts/LinkedContracts';
import CreateBunkerStemForm from '@client/components/CreateBunkerStemForm/CreateBunkerStemForm';
import RobEntryDrawer from '../components/RobEntryDrawer';
import { useVoyage } from '../../components/VoyageProvider/VoyageProvider';
import styles from './BunkerExpenditureTab.module.css';
import {
	getExpandedRow,
	getTableColumns,
} from './getTableColumns';
import { transformRobToBunkerStem } from './helpers';

export type AdjustBunkerAmountAttributes = {
	robBunkerId: number;
	portCallActionId: number;
	adjustment: number;
	bunkerId?: number;
};

export type EditValues = {
	id: number;
	event?: string | null;
	port?: Port | null;
	date?: Moment;
	quantity?: number;
	fuelGrade?: Values<typeof FuelTypes>;
	robs?: Array<RobBunkerProps>;
	robBunkerId?: number;
	pricePerTon?: number;
	allowSetPrice?: boolean;
	bunkerId?: number;
	portCallActionId?: number;
	bunkerStemId?: number | null;
	VesselBunkers?: Array<{
		quantity?: number;
		fuelGrade?: Values<typeof FuelTypes>;
		pricePerTon?: number;
		Bunker: BunkerProps;
	}>;
};

const BunkerExpenditureTab = () => {
	const {
		voyageDetails,
		refreshVendorExpenses,
	} = useVoyage();

	const { vessel } = voyageDetails;
	const fixtureCurrency = voyageDetails == null ?
		Currencies.USD :
		voyageDetails.bankAccount.currency;

	const [robDrawerOpen, setRobDrawerOpen] = useState(false);
	const [stemDrawerOpen, setStemDrawerOpen] = useState(false);
	const [editingRob, setEditingRob] = useState <null | EditValues>(null);
	const [editingStem, setEditingStem] = useState <null| undefined | ReturnStem>(null);

	const {
		data: bunkerRobs,
		refetch: refreshBunkerRobs,
	} = useQuery(
		['robs', voyageDetails.id],
		async () => getRobs(vessel.id, voyageDetails.id),
		{
			enabled: vessel?.id != null && voyageDetails?.id != null,
			refetchOnMount: true,
		},
	);

	const robs = useMemo(() => {
		if (bunkerRobs == null) {
			return [];
		}

		return bunkerRobs.expenditures;
	}, [bunkerRobs]);

	const rowLength = useMemo(() => {
		if (robs == null) {
			return 0;
		}

		const unique = filterUnique(
			robs.flatMap((r) => r.RobBunkers),
			(rb) => rb.fuelGrade,
		);

		return unique.length;
	}, [robs]);

	const blockBunkersTab = voyageDetails.commencementDate == null;

	const handleBeginEdit = useCallback((portCallActionId: number) => {
		const rob = bunkerRobs?.robs.find((r) => r.id === portCallActionId);

		if (rob == null) {
			return;
		}

		if (rob.bunkerStemId != null) {
			setEditingStem(transformRobToBunkerStem(rob, voyageDetails.bankAccount.currency));
			setStemDrawerOpen(true);
		} else {
			const vesselBunker = rob.VesselBunkers.find((vb) => vb.portCallActionId === rob.id);
			setEditingRob({
				...rob,
				robs: rob.RobBunkers,
				allowSetPrice: false,
				portCallActionId: rob.id,
				bunkerId: vesselBunker?.bunkerId,
			});
		}
	}, [bunkerRobs?.robs, voyageDetails?.bankAccount?.currency]);

	const firstRob = robs?.[0];
	const acceptableFuels = firstRob?.RobBunkers.map((rb) => rb.fuelGrade);

	const emptyContent = useMemo(() => {
		let detailedText = (
			<>
				To get started, set the actual commencement date
				by connecting this contract to a previous contract.
			</>
		);

		const { previousVoyage } = voyageDetails;

		if (previousVoyage != null && previousVoyage.id !== -1) {
			detailedText = (
				<>
					You still need to add an actual completion date for
					{' '}
					<Link to={Links.Voyage.get(previousVoyage.id)}>
						{previousVoyage.identifier}
					</Link>
				</>
			);
		}

		return (
			<>
				<Col span={24}>
					You cannot edit bunkers at this time.
					<br />
					{detailedText}
				</Col>
				<Col span={24}>
					<SummaryCardExtra />
				</Col>
			</>
		);
	}, [voyageDetails]);

	if (blockBunkersTab) {
		return (
			<div className={styles.emptyWrapper}>
				<Card>
					<Empty
						className={styles.emptyMessage}
						description={(
							<Row gutter={[16, 16]}>
								{emptyContent}
							</Row>
						)}
					/>
				</Card>
			</div>
		);
	}

	return (
		<>
			<RobEntryDrawer
				setRobDrawerOpen={setRobDrawerOpen}
				open={robDrawerOpen}
				fixtureCurrency={fixtureCurrency}
				refreshData={refreshBunkerRobs}
				vesselId={vessel.id}
				editing={editingRob}
				setEditing={setEditingRob}
				voyageDetails={voyageDetails}
				acceptableFuels={acceptableFuels}
			/>
			<CreateBunkerStemForm
				voyageId={voyageDetails.id}
				refreshDetails={() => {
					refreshBunkerRobs();
					refreshVendorExpenses();
				}}
				open={stemDrawerOpen}
				onClose={() => {
					setStemDrawerOpen(false);
				}}
				tcInContract={voyageDetails?.tcInContract}
				editingStem={editingStem}
				setEditingStem={setEditingStem}
				fixtureCurrency={voyageDetails.bankAccount.currency}
			/>
			<Flex vertical gap={10}>
				<div style={{ width: 'fit-content' }}>
					<LinkedContracts
						voyageDetails={voyageDetails}
					/>
				</div>
				{robs.length === 0 ? (
					<Card className={styles.center}>
						<b>We could not find any events from the ship, with ROB information.</b>
						<br />
						<br />
						<AddButton
							onClick={() => setRobDrawerOpen(true)}
						>
							Add new
						</AddButton>
					</Card>
				) : (
					<Card slim>
						<Table
							bordered
							columns={getTableColumns(robs, voyageDetails, handleBeginEdit)}
							className={styles.row}
							dataSource={robs}
							pagination={false}
							rowKey="id"
							expandable={{
								expandedRowRender: (row) => getExpandedRow(
									row,
									rowLength,
								),
								rowExpandable: () => true,
							}}
							tableLayout="fixed"
							size="small"
							data-testid="bunker-expenditure-table"
						/>
					</Card>
				)}
			</Flex>
		</>
	);
};

export default BunkerExpenditureTab;
